import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { WrappedFieldArrayProps } from 'redux-form'
// hooks
import { useCurrentForm } from 'hooks/index'
import { useNetworkStatus } from 'hooks/useNetworkStatus'
import { useRowOffset, useRowsHeight } from '../hooks'
// components
import Row from './components/Row'
import AddRowButton from '../AddRowButton'
// context
import { PublicContext } from 'containers/Risks/Risk/Edit/public'
import { RiskAPIContext } from 'containers/Risks/Risk/Edit/layers/API'
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// views
import { RowsWrapper } from './views'
import { OuterWrapper } from 'components/PageComponents/RiskTemplates/Edit/components/RiskTemplateFields/components/Table/components/TableForm/components/Rows/components/Row/views'
// utils
import { getRowColor } from '../utils'

interface Props extends WrappedFieldArrayProps<any> {
  bg?: string
  disabled?: boolean
  parentId?: string
}

/** Render row (FieldArray) for every fieldLink parent field */

const Rows: React.FC<Props> = ({ bg, fields, disabled, parentId }) => {
  const router = useRouter()

  const { addRow, fieldLink, linked } = useCurrentForm()

  const isOnline = useNetworkStatus()

  const isPublic = React.useContext(PublicContext)

  const { risk } = React.useContext(RiskAPIContext)
  const { riskTemplate, isRisksRegister, isRiskTemplate, linkedRiskTemplates } =
    React.useContext(RiskTemplateAPIContext)

  const { ref } = useRowsHeight({ groupId: parentId, rowId: `${parentId}-${fieldLink.id}` })

  const offset = useRowOffset({
    rowId: parentId ? `${parentId}-${fieldLink.id}` : undefined,
    groupId: parentId
  })

  const isRegisters = isRisksRegister || !!router.query.multiple
  const canEditRisk = risk?.permissions.riskModify && risk?.canUpdateFromSystemProcesses
  const isPrimaryOrEmpty = fieldLink.primary ? true : fields.length < 1
  const parent = risk?.fieldValueMap?.find((i) => i.fieldValueId === parentId)
  const parentArchived = parent?.archived
  const showAddButton = !isRegisters && isPrimaryOrEmpty && !parentArchived
  const addRowDisabled = !isOnline || disabled || isPublic || (!isRiskTemplate && !canEditRisk)

  const fieldsWithAi = React.useMemo(() =>
      !!linkedRiskTemplates
        .find((item) => item.id === fieldLink.field.entityId)
        ?.fields?.filter((item) =>
          item.active &&
          item.includeInAiContext &&
          riskTemplate.fieldLinks
            .filter((link) => link.field.entityId === fieldLink.field.entityId)
            .some((link) => link.field.id === item.id)
        ).length,
    [linkedRiskTemplates, riskTemplate.fieldLinks, fieldLink.field.entityId]
  )

  const canGenerate = !addRowDisabled && showAddButton && linked && fieldsWithAi

  const removeRow = (idx: number) => () => fields.remove(idx)

  return (
    <OuterWrapper $bg={bg}>
      <RowsWrapper
        ref={ref}
        $mb={offset}
        data-parent-id={parentId}
        data-testid='risk-table-form-rows'
        data-entity-id={fieldLink.field.id}>
        {fields.map((name, idx) => (
          <Row
            key={name}
            name={name}
            deleteRow={removeRow(idx)}
            parentArchived={parentArchived}
            bg={!parentId ? getRowColor(idx) : undefined}
          />
        ))}
        {showAddButton && (
          <AddRowButton
            disabled={addRowDisabled}
            canGenerate={canGenerate}
            newRecordLabel={fieldLink.addFieldLabel}
            onAddClick={addRow(fields.name, parentId)}
            contextRecordId={parent?.fieldValue?.value || risk?.id}
          />
        )}
      </RowsWrapper>
    </OuterWrapper>
  )
}

export default Rows
